import React, { useState } from 'react';
import {
  BellAlertIcon,
  CheckCircleIcon,
  DevicePhoneMobileIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import axios from 'axios';
import { usePushNotifications } from '../../contexts/PushNotificationContext';
import { useToast } from '../../contexts/ToastContext';
import { getErrorMessage } from '../../utils/errorMessage';
import api from '../../services/api';
import { logPushTestResult, logPushTestFailure } from '../../services/pushDiagnostics';

/**
 * A push műveletek kétféle hibát adhatnak: axios hibát a backendtől, illetve
 * a böngésző korlátaiból fakadó saját Error-t (pl. hiányzó engedély).
 */
const describeError = (error: unknown, fallback: string): string => {
  if (axios.isAxiosError(error)) {
    return getErrorMessage(error, fallback);
  }
  return error instanceof Error && error.message ? error.message : fallback;
};

interface PushNotificationSettingsProps {
  /** A fiók szintű kapcsoló kezdeti értéke (profil végpontból). */
  initialEnabled: boolean;
}

/**
 * Push értesítések beállítása a profil oldalon:
 * - eszköz szintű be-/kikapcsolás (böngésző engedély + feliratkozás),
 * - fiók szintű néma kapcsoló (minden eszközre),
 * - a feliratkozott eszközök listája.
 */
const PushNotificationSettings: React.FC<PushNotificationSettingsProps> = ({ initialEnabled }) => {
  const {
    supported,
    needsHomeScreenInstall,
    subscribed,
    devices,
    unavailableReason,
    busy,
    loading,
    enable,
    disable,
    sendTest,
    removeDevice,
  } = usePushNotifications();
  const { showToast } = useToast();

  const [accountEnabled, setAccountEnabled] = useState(initialEnabled);
  const [savingAccountToggle, setSavingAccountToggle] = useState(false);

  const handleEnable = async () => {
    try {
      await enable();
      showToast('Push értesítések bekapcsolva ezen az eszközön.');
    } catch (error) {
      showToast(describeError(error, 'Nem sikerült bekapcsolni az értesítéseket.'), 'error');
    }
  };

  const handleDisable = async () => {
    try {
      await disable();
      showToast('Push értesítések kikapcsolva ezen az eszközön.', 'info');
    } catch (error) {
      showToast(describeError(error, 'Nem sikerült kikapcsolni az értesítéseket.'), 'error');
    }
  };

  /**
   * A teszt eredménye mindkét ágon a konzolra kerül, a böngésző és a szerver
   * oldali adatokkal együtt: egy meg nem érkező értesítés oka a lánc bármely
   * pontján lehet, és a toast ebből csak egy mondatot tud mutatni.
   */
  const handleTest = async () => {
    try {
      const result = await sendTest();
      await logPushTestResult(result);

      if (result.success) {
        const { delivered, subscriptionsTargeted } = result.result;
        showToast(
          `Teszt értesítés kiküldve ${delivered}/${subscriptionsTargeted} eszközre. Részletek a böngésző konzoljában.`,
          'success'
        );
      } else {
        showToast(
          `${result.reason ?? 'A teszt értesítés nem ment ki.'} Részletek a böngésző konzoljában.`,
          'error'
        );
      }
    } catch (error) {
      const message = describeError(error, 'A teszt értesítést nem sikerült elküldeni.');
      await logPushTestFailure(error, message);
      showToast(`${message} Részletek a böngésző konzoljában.`, 'error');
    }
  };

  const handleRemoveDevice = async (id: number) => {
    try {
      await removeDevice(id);
      showToast('Eszköz eltávolítva.', 'info');
    } catch (error) {
      showToast(describeError(error, 'Az eszközt nem sikerült eltávolítani.'), 'error');
    }
  };

  const handleAccountToggle = async (value: boolean) => {
    setAccountEnabled(value);
    setSavingAccountToggle(true);

    try {
      await api.put('/profile', { pushNotificationsEnabled: value });
      showToast(value ? 'Push értesítések engedélyezve.' : 'Push értesítések szüneteltetve.', 'info');
    } catch (error) {
      setAccountEnabled(!value);
      showToast(describeError(error, 'A beállítás mentése nem sikerült.'), 'error');
    } finally {
      setSavingAccountToggle(false);
    }
  };

  return (
    <div className="bg-white shadow-sm rounded-xl overflow-hidden">
      <div className="px-4 md:px-6 py-4 border-b border-gray-100 card-header bg-gradient-to-r from-indigo-50 to-white">
        <div className="flex items-center space-x-3">
          <div className="card-header-icon p-2 bg-indigo-100 rounded-lg">
            <BellAlertIcon className="h-6 w-6 text-indigo-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Push értesítések</h2>
            <p className="text-sm text-gray-500">
              Azonnali értesítés a telefonra vagy gépre, akkor is, ha az app nincs megnyitva.
            </p>
          </div>
        </div>
      </div>

      <div className="p-4 md:p-6 space-y-6">
        {loading ? (
          <div className="h-10 bg-gray-100 rounded-lg animate-pulse" />
        ) : (
          <>
            {/* Eszköz szintű állapot és művelet */}
            {needsHomeScreenInstall ? (
              <InfoBox
                tone="info"
                title="Előbb tedd ki az appot a kezdőképernyőre"
                description="iPhone és iPad esetén az értesítések csak telepített appból működnek: Safariban koppints a Megosztás gombra, majd a „Főképernyőhöz adás” lehetőségre. Ezután nyisd meg az appot a kezdőképernyőről, és itt kapcsold be az értesítéseket."
              />
            ) : !supported ? (
              <InfoBox
                tone="info"
                title="Ez a böngésző nem támogatja a push értesítéseket"
                description="Próbáld meg Chrome, Edge, Firefox vagy Safari friss verziójával."
              />
            ) : unavailableReason === 'server-disabled' ? (
              <InfoBox
                tone="warning"
                title="A push értesítések nincsenek beállítva a szerveren"
                description="Az adminisztrátornak be kell állítania a VAPID kulcsokat, utána válik elérhetővé a funkció."
              />
            ) : unavailableReason === 'permission-denied' ? (
              <InfoBox
                tone="warning"
                title="Az értesítések le vannak tiltva ebben a böngészőben"
                description="A címsor melletti lakat ikonnál (vagy a böngésző oldalbeállításainál) engedélyezd újra az értesítéseket, majd frissítsd az oldalt."
              />
            ) : (
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 p-4 rounded-lg border border-gray-200 bg-gray-50">
                <div className="flex items-start space-x-3">
                  <DevicePhoneMobileIcon className="h-6 w-6 text-gray-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-medium text-gray-900">Ez az eszköz</div>
                    <div className="text-sm text-gray-500">
                      {subscribed
                        ? 'Az értesítések be vannak kapcsolva ezen az eszközön.'
                        : 'Kapcsold be, hogy erre az eszközre is érkezzenek értesítések.'}
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  {subscribed && (
                    <button
                      type="button"
                      onClick={handleTest}
                      disabled={busy}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      Teszt
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={subscribed ? handleDisable : handleEnable}
                    disabled={busy}
                    className={`px-4 py-2 text-sm font-medium rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${
                      subscribed
                        ? 'text-red-700 bg-white border border-red-300 hover:bg-red-50'
                        : 'text-white bg-primary-600 hover:bg-primary-700'
                    }`}
                  >
                    {busy ? 'Folyamatban...' : subscribed ? 'Kikapcsolás' : 'Bekapcsolás'}
                  </button>
                </div>
              </div>
            )}

            {/* Fiók szintű kapcsoló */}
            <label className="flex items-start space-x-3 cursor-pointer">
              <input
                type="checkbox"
                checked={accountEnabled}
                disabled={savingAccountToggle}
                onChange={(e) => handleAccountToggle(e.target.checked)}
                className="mt-1 h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
              />
              <div>
                <div className="font-medium text-gray-900">Push értesítések fogadása</div>
                <div className="text-sm text-gray-500">
                  Ha kikapcsolod, egyik eszközödre sem küldünk push értesítést - a feliratkozások megmaradnak.
                </div>
              </div>
            </label>

            {/* Feliratkozott eszközök */}
            {devices.length > 0 && (
              <div>
                <div className="text-sm font-medium text-gray-700 mb-2">Feliratkozott eszközök</div>
                <ul className="divide-y divide-gray-100 border border-gray-200 rounded-lg overflow-hidden">
                  {devices.map((device) => (
                    <li key={device.id} className="flex items-center justify-between px-4 py-3 bg-white">
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-gray-900 truncate">
                          {device.deviceName || 'Ismeretlen eszköz'}
                        </div>
                        <div className="text-xs text-gray-500">
                          Utoljára használva:{' '}
                          {new Date(device.lastUsedAt).toLocaleDateString('hu-HU', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                          })}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleRemoveDevice(device.id)}
                        disabled={busy}
                        className="p-2 text-gray-400 hover:text-red-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        aria-label="Eszköz eltávolítása"
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

interface InfoBoxProps {
  tone: 'info' | 'warning' | 'success';
  title: string;
  description: string;
}

const InfoBox: React.FC<InfoBoxProps> = ({ tone, title, description }) => {
  const styles = {
    info: { box: 'bg-blue-50 text-blue-900', icon: <InformationCircleIcon className="h-5 w-5 text-blue-500" /> },
    warning: { box: 'bg-amber-50 text-amber-900', icon: <ExclamationTriangleIcon className="h-5 w-5 text-amber-500" /> },
    success: { box: 'bg-green-50 text-green-900', icon: <CheckCircleIcon className="h-5 w-5 text-green-500" /> },
  }[tone];

  return (
    <div className={`p-4 rounded-lg flex items-start space-x-3 ${styles.box}`}>
      <div className="flex-shrink-0 mt-0.5">{styles.icon}</div>
      <div>
        <div className="font-medium">{title}</div>
        <div className="text-sm mt-1 opacity-90">{description}</div>
      </div>
    </div>
  );
};

export default PushNotificationSettings;
